
import React, { useState, useEffect } from 'react';
import { Edit2, Eye, Trophy, LayoutGrid, List, Tag, Minus, Plus, Users } from 'lucide-react';
import ResultsView from './ResultsView';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

// --- Hook mobile ---
function useMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
    useEffect(() => {
        const handle = () => setIsMobile(window.innerWidth < 700);
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);
    return isMobile;
}

// --- Helpers ---
const getAthleteInfo = (id, allAthletes) => {
    if (!id) return null;
    const a = allAthletes?.find(a => String(a.id) === String(id));
    if (!a) return null;
    return {
        name: `${(a.nom || a.name || '').toUpperCase()} ${a.prenom || a.firstname || ''}`.trim(),
        club: a.club || ''
    };
};

const resolveMatchName = (id, placeholder, allAthletes, allMatches, sourceMatchId) => {
    if (id) {
        const info = getAthleteInfo(id, allAthletes);
        if (info) return info;
    }
    if (sourceMatchId) {
        const src = allMatches?.find(m => String(m.id) === String(sourceMatchId));
        if (src) return { name: `Vainqueur V${src.match_ref}`, club: '' };
    }
    return { name: placeholder || 'En attente', club: '' };
};

// --- WarningSelector ---
const WarningSelector = ({ value, onChange, color }) => (
    <div style={{ textAlign: 'center', marginBottom: '15px' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '8px' }}>AVT</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
            <button type="button" onClick={() => onChange(Math.max(0, value - 1))}
                style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #cbd5e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: 'white' }}>
                <Minus size={16} color="#4a5568" />
            </button>
            <div style={{ fontSize: '24px', fontWeight: '900', color, minWidth: '40px' }}>
                {value} <span style={{ fontSize: '14px' }}>A</span>
            </div>
            <button type="button" onClick={() => onChange(Math.min(3, value + 1))}
                style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #cbd5e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: 'white' }}>
                <Plus size={16} color="#4a5568" />
            </button>
        </div>
    </div>
);

// --- DecisionModalContent ---
const DecisionModalContent = ({ athlete1Name, athlete2Name, initialData, onConfirm }) => {
    const [winnerSide, setWinnerSide] = useState(initialData?.winnerSide || null);
    const [type, setType] = useState(initialData?.type || 'unanimite');
    const [warnA1, setWarnA1] = useState(initialData?.warningsA1 || 0);
    const [warnA2, setWarnA2] = useState(initialData?.warningsA2 || 0);
    return (
        <div style={{ textAlign: 'left', fontFamily: 'sans-serif', padding: '10px' }}>
            <div style={{ marginBottom: '20px', padding: '15px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b', textTransform: 'uppercase' }}>Désigner le vainqueur</h4>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setWinnerSide('A1')}
                        style={{ flex: 1, padding: '12px', borderRadius: '8px', border: winnerSide === 'A1' ? '2px solid #e53e3e' : '1px solid #e2e8f0', backgroundColor: winnerSide === 'A1' ? '#fff5f5' : 'white', color: '#e53e3e', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>
                        {athlete1Name}
                    </button>
                    <button onClick={() => setWinnerSide('A2')}
                        style={{ flex: 1, padding: '12px', borderRadius: '8px', border: winnerSide === 'A2' ? '2px solid #3182ce' : '1px solid #e2e8f0', backgroundColor: winnerSide === 'A2' ? '#ebf8ff' : 'white', color: '#3182ce', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>
                        {athlete2Name}
                    </button>
                </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#64748b' }}>TYPE DE DÉCISION</h4>
                <select value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <option value="unanimite">Unanimité des juges</option>
                    <option value="majorite">Majorité des juges</option>
                    <option value="abandon">Abandon</option>
                    <option value="hors_combat">Hors-Combat (HC)</option>
                    <option value="jet_eponge">Jet d'éponge</option>
                    <option value="disqualification">Disqualification</option>
                </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <WarningSelector value={warnA1} onChange={setWarnA1} color="#e53e3e" />
                <WarningSelector value={warnA2} onChange={setWarnA2} color="#3182ce" />
            </div>
            <button disabled={!winnerSide} onClick={() => onConfirm({ winnerSide, type, warningsA1: warnA1, warningsA2: warnA2 })}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: winnerSide ? '#38a169' : '#cbd5e0', color: 'white', fontWeight: 'bold', fontSize: '16px', cursor: winnerSide ? 'pointer' : 'not-allowed' }}>
                VALIDER LE RÉSULTAT
            </button>
        </div>
    );
};

const openDecisionModal = (match, allAthletes, allMatches, isCorrection, onUpdateWinner) => {
    if (!match.athlete1_id || !match.athlete2_id) {
        Swal.fire('Assaut incomplet', 'Attendez que les deux tireurs soient qualifiés.', 'warning');
        return;
    }
    const a1 = allAthletes?.find(a => String(a.id) === String(match.athlete1_id));
    const a2 = allAthletes?.find(a => String(a.id) === String(match.athlete2_id));
    const name1 = a1 ? `${(a1.nom || a1.name || '').toUpperCase()} ${a1.prenom || ''}`.trim() : (match.placeholder1 || 'ROUGE');
    const name2 = a2 ? `${(a2.nom || a2.name || '').toUpperCase()} ${a2.prenom || ''}`.trim() : (match.placeholder2 || 'BLEU');
    const initialData = isCorrection ? {
        winnerSide: String(match.winner_id) === String(match.athlete1_id) ? 'A1' : 'A2',
        type: match.decision_type || 'unanimite',
        warningsA1: match.warnings_a1 || 0,
        warningsA2: match.warnings_a2 || 0
    } : null;
    MySwal.fire({
        title: isCorrection ? 'Correction du résultat' : 'Désigner le vainqueur',
        showConfirmButton: false,
        showCloseButton: true,
        html: (
            <DecisionModalContent athlete1Name={name1} athlete2Name={name2} initialData={initialData}
                onConfirm={data => {
                    const winnerId = data.winnerSide === 'A1' ? match.athlete1_id : match.athlete2_id;
                    onUpdateWinner(match, winnerId, data);
                    MySwal.close();
                }}
            />
        )
    });
};


// =========================================================
// VUE D'ENSEMBLE
// =========================================================
function OverviewView({ order, allAthletes, rings, isStarted, readOnly, onUpdateWinner, isMobile }) {
    const total = order.length;
    const completed = order.filter(m => m.winner_id).length;
    const remaining = total - completed;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    return (
        <div style={{ padding: isMobile ? '12px' : '20px' }}>
            {/* Statistiques globales */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: isMobile ? '8px' : '12px', marginBottom: '16px' }}>
                {[
                    { label: 'Total', val: total, color: '#4a5568', bg: '#f8fafc', border: '#e2e8f0' },
                    { label: 'Terminés', val: completed, color: '#276749', bg: '#f0fff4', border: '#9ae6b4' },
                    { label: 'Restants', val: remaining, color: '#c53030', bg: '#fff5f5', border: '#feb2b2' }
                ].map(s => (
                    <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: '10px', padding: isMobile ? '10px 6px' : '14px 10px', textAlign: 'center', border: `1px solid ${s.border}` }}>
                        <div style={{ fontSize: isMobile ? '24px' : '30px', fontWeight: '900', color: s.color, lineHeight: 1 }}>{s.val}</div>
                        <div style={{ fontSize: '10px', color: '#718096', fontWeight: '600', textTransform: 'uppercase', marginTop: '5px' }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Barre de progression globale */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11px', color: '#718096', fontWeight: '600' }}>Progression globale</span>
                    <span style={{ fontSize: '11px', color: '#276749', fontWeight: '700' }}>{progress}%</span>
                </div>
                <div style={{ backgroundColor: '#e2e8f0', borderRadius: '20px', height: '8px', overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#48bb78', borderRadius: '20px', transition: 'width 0.5s ease' }} />
                </div>
            </div>

            {/* Cartes par ring */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: isMobile ? '12px' : '16px' }}>
                {rings.map(ringId => {
                    const ringMatches = order.filter(m => String(m.ring_id) === String(ringId));
                    const ringCompleted = ringMatches.filter(m => m.winner_id).length;
                    const ringTotal = ringMatches.length;
                    const pending = ringMatches.filter(m => !m.winner_id).sort((a, b) => parseInt(a.match_ref) - parseInt(b.match_ref));
                    const current = pending[0] || null;
                    const next = pending.slice(1, 4);
                    const ringPct = ringTotal > 0 ? Math.round((ringCompleted / ringTotal) * 100) : 0;

                    return (
                        <div key={ringId} style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <div style={{ backgroundColor: '#1a202c', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.05em' }}>RING {ringId}</span>
                                <span style={{ color: '#a0aec0', fontSize: '12px', fontWeight: '600' }}>{ringCompleted} / {ringTotal}</span>
                            </div>
                            <div style={{ height: '4px', backgroundColor: '#e2e8f0' }}>
                                <div style={{ width: `${ringPct}%`, height: '100%', backgroundColor: '#48bb78', transition: 'width 0.5s ease' }} />
                            </div>

                            <div style={{ padding: '14px' }}>
                                {current ? (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                                            <span style={{ backgroundColor: '#fed7d7', color: '#9b2c2c', padding: '3px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 'bold' }}>● EN COURS</span>
                                            <span style={{ backgroundColor: '#2d3748', color: 'white', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', letterSpacing: '0.03em' }}>
                                                ASSAUT {current.match_ref}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#3182ce', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>
                                            {current.category_name} — {current.poule_label}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                            {(() => { const info = resolveMatchName(current.athlete1_id, current.placeholder1, allAthletes, order, current.source_match_id); return (
                                                <div style={{ flex: 1, minWidth: 0, textAlign: 'center', backgroundColor: '#fff5f5', padding: '8px 6px', borderRadius: '8px', border: '1px solid #feb2b2', overflow: 'hidden' }}>
                                                    <div style={{ fontWeight: '900', fontSize: isMobile ? '12px' : '13px', color: '#c53030', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.name}</div>
                                                    {info.club && <div style={{ fontSize: '10px', color: '#e57373', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.club}</div>}
                                                </div>
                                            ); })()}
                                            <div style={{ fontWeight: 'bold', color: '#cbd5e0', fontSize: '11px', flexShrink: 0 }}>VS</div>
                                            {(() => { const info = resolveMatchName(current.athlete2_id, current.placeholder2, allAthletes, order, current.source_match_id2); return (
                                                <div style={{ flex: 1, minWidth: 0, textAlign: 'center', backgroundColor: '#ebf8ff', padding: '8px 6px', borderRadius: '8px', border: '1px solid #bee3f8', overflow: 'hidden' }}>
                                                    <div style={{ fontWeight: '900', fontSize: isMobile ? '12px' : '13px', color: '#2b6cb0', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.name}</div>
                                                    {info.club && <div style={{ fontSize: '10px', color: '#63b3ed', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.club}</div>}
                                                </div>
                                            ); })()}
                                        </div>
                                        {!readOnly && isStarted && (
                                            <button onClick={() => openDecisionModal(current, allAthletes, order, false, onUpdateWinner)}
                                                style={{ width: '100%', padding: '9px', borderRadius: '8px', border: 'none', backgroundColor: '#38a169', color: 'white', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '12px' }}>
                                                <Trophy size={13} /> DÉCISION / VICTOIRE
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '16px', color: '#68d391', fontWeight: '700', fontSize: '14px' }}>Ring terminé ✓</div>
                                )}
                            </div>

                            {next.length > 0 && (
                                <div style={{ borderTop: '1px dashed #e2e8f0', padding: '10px 14px', backgroundColor: '#f8fafc' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#a0aec0', marginBottom: '6px', textTransform: 'uppercase' }}>Prochains assauts</div>
                                    {next.map(m => {
                                        const a1 = resolveMatchName(m.athlete1_id, m.placeholder1, allAthletes, order, m.source_match_id);
                                        const a2 = resolveMatchName(m.athlete2_id, m.placeholder2, allAthletes, order, m.source_match_id2);
                                        return (
                                            <div key={m.id} style={{ marginBottom: '7px', paddingBottom: '7px', borderBottom: '1px solid #edf2f7' }}>
                                                <div style={{ marginBottom: '3px' }}>
                                                    <span style={{ backgroundColor: '#2d3748', color: 'white', borderRadius: '4px', padding: '2px 7px', fontWeight: 'bold', fontSize: '10px' }}>Assaut {m.match_ref}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '11px' }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ color: '#e53e3e', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a1.name}</div>
                                                        {a1.club && <div style={{ fontSize: '9px', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a1.club}</div>}
                                                    </div>
                                                    <span style={{ color: '#cbd5e0', fontSize: '10px', flexShrink: 0, paddingTop: '1px' }}>vs</span>
                                                    <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                                                        <div style={{ color: '#3182ce', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a2.name}</div>
                                                        {a2.club && <div style={{ fontSize: '9px', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a2.club}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {pending.length > 4 && <div style={{ fontSize: '10px', color: '#a0aec0', fontStyle: 'italic', marginTop: '3px' }}>+ {pending.length - 4} suivant(s)</div>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


// =========================================================
// VUE PAR RING
// =========================================================
function RingDetailView({ order, allAthletes, rings, isStarted, readOnly, onUpdateWinner, isMobile }) {
    const [selectedRing, setSelectedRing] = useState(rings[0]);

    useEffect(() => {
        if (rings.length > 0 && !rings.includes(selectedRing)) setSelectedRing(rings[0]);
    }, [rings]); // eslint-disable-line react-hooks/exhaustive-deps

    const ringMatches = order.filter(m => String(m.ring_id) === String(selectedRing)).sort((a, b) => parseInt(a.match_ref) - parseInt(b.match_ref));
    const currentMatch = ringMatches.find(m => !m.winner_id);

    return (
        <div style={{ padding: isMobile ? '12px' : '20px' }}>
            {/* Sélecteur de ring */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {rings.map(r => {
                    const remaining = order.filter(m => String(m.ring_id) === String(r) && !m.winner_id).length;
                    const isActive = String(selectedRing) === String(r);
                    return (
                        <button key={r} onClick={() => setSelectedRing(r)}
                            style={{ padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', backgroundColor: isActive ? '#1a202c' : '#edf2f7', color: isActive ? 'white' : '#4a5568', fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                            Ring {r}
                            <span style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : '#cbd5e0', color: isActive ? 'white' : '#4a5568', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 'bold' }}>
                                {remaining}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {ringMatches.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0' }}>Aucun assaut pour ce ring</div>
                ) : ringMatches.map((m, idx) => {
                    const isDone = !!m.winner_id;
                    const isCurrent = !isDone && m.id === currentMatch?.id;
                    const isWinner1 = String(m.winner_id) === String(m.athlete1_id);
                    const a1 = resolveMatchName(m.athlete1_id, m.placeholder1, allAthletes, order, m.source_match_id);
                    const a2 = resolveMatchName(m.athlete2_id, m.placeholder2, allAthletes, order, m.source_match_id2);

                    return (
                        <div key={m.id} style={{ borderRadius: '10px', border: isCurrent ? '2px solid #48bb78' : '1px solid #e2e8f0', backgroundColor: isCurrent ? 'white' : isDone ? '#f8fafc' : 'white', padding: isMobile ? '10px 12px' : '11px 14px', display: 'flex', alignItems: 'center', gap: '10px', opacity: isDone ? 0.75 : 1, boxShadow: isCurrent ? '0 0 0 4px rgba(72,187,120,0.12)' : 'none' }}>
                            <div style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isDone ? '#48bb78' : isCurrent ? '#38a169' : '#e2e8f0', color: (isDone || isCurrent) ? 'white' : '#718096', fontWeight: 'bold', fontSize: '12px' }}>
                                {isDone ? '✓' : isCurrent ? '▶' : idx + 1}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                    {isCurrent && <span style={{ backgroundColor: '#c6f6d5', color: '#276749', padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold' }}>EN COURS</span>}
                                    <span style={{ backgroundColor: '#2d3748', color: 'white', padding: '2px 8px', borderRadius: '5px', fontSize: '12px', fontWeight: 'bold' }}>Assaut {m.match_ref}</span>
                                    <span style={{ fontSize: '10px', color: '#3182ce', fontWeight: 'bold' }}>{m.category_name}</span>
                                    <span style={{ fontSize: '10px', color: '#a0aec0' }}>{m.poule_label}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: '800', fontSize: '13px', color: isDone ? (isWinner1 ? '#c53030' : '#a0aec0') : '#e53e3e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {isDone && isWinner1 && '🏆 '}{a1.name}
                                        </div>
                                        {a1.club && <div style={{ fontSize: '10px', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a1.club}</div>}
                                    </div>
                                    <span style={{ color: '#cbd5e0', fontSize: '11px', flexShrink: 0, paddingTop: '1px' }}>vs</span>
                                    <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                                        <div style={{ fontWeight: '800', fontSize: '13px', color: isDone ? (!isWinner1 ? '#2b6cb0' : '#a0aec0') : '#3182ce', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {isDone && !isWinner1 && '🏆 '}{a2.name}
                                        </div>
                                        {a2.club && <div style={{ fontSize: '10px', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a2.club}</div>}
                                    </div>
                                </div>
                                {isDone && m.decision_type && (
                                    <div style={{ fontSize: '10px', color: '#718096', marginTop: '2px', fontStyle: 'italic' }}>
                                        {m.decision_type.replace(/_/g, ' ')}{(m.warnings_a1 > 0 || m.warnings_a2 > 0) && ` · AVT: ${m.warnings_a1}/${m.warnings_a2}`}
                                    </div>
                                )}
                            </div>
                            {!readOnly && (
                                <>
                                    {isCurrent && isStarted && (
                                        <button onClick={() => openDecisionModal(m, allAthletes, order, false, onUpdateWinner)}
                                            style={{ padding: '7px 12px', borderRadius: '8px', border: 'none', backgroundColor: '#38a169', color: 'white', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                                            <Trophy size={12} /> {isMobile ? '' : 'Décision'}
                                        </button>
                                    )}
                                    {isDone && (
                                        <button onClick={() => openDecisionModal(m, allAthletes, order, true, onUpdateWinner)}
                                            style={{ padding: '6px 9px', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', color: '#3182ce', flexShrink: 0 }} title="Corriger">
                                            <Edit2 size={12} />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


// =========================================================
// VUE PAR CATÉGORIE
// =========================================================
function CategoryView({ order, allAthletes, categories, isMobile }) {
    const [selectedCatId, setSelectedCatId] = useState(categories[0]?.id || null);

    useEffect(() => {
        if (categories.length > 0 && !categories.find(c => String(c.id) === String(selectedCatId))) {
            setSelectedCatId(categories[0].id);
        }
    }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

    const catMatches = order.filter(m => String(m.category_id) === String(selectedCatId)).sort((a, b) => parseInt(a.match_ref) - parseInt(b.match_ref));
    const byPoule = catMatches.reduce((acc, m) => {
        const k = m.poule_label || 'Poule Unique';
        if (!acc[k]) acc[k] = [];
        acc[k].push(m);
        return acc;
    }, {});

    return (
        <div style={{ padding: isMobile ? '12px' : '20px' }}>
            {/* Dropdown catégorie */}
            <div style={{ marginBottom: '16px' }}>
                <select value={selectedCatId || ''} onChange={e => setSelectedCatId(e.target.value)}
                    style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '15px', fontWeight: '600', width: '100%', maxWidth: isMobile ? '100%' : '400px', backgroundColor: 'white', cursor: 'pointer' }}>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>

            {catMatches.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0', fontStyle: 'italic' }}>Aucun assaut pour cette catégorie</div>
            ) : (
                Object.entries(byPoule).map(([pouleLabel, matches]) => {
                    const doneCount = matches.filter(m => m.winner_id).length;
                    return (
                        <div key={pouleLabel} style={{ marginBottom: '16px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div style={{ backgroundColor: '#ebf8ff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #bee3f8' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#2b6cb0', textTransform: 'uppercase' }}>{pouleLabel}</span>
                                <span style={{ fontSize: '11px', color: '#4299e1', fontWeight: '600' }}>{doneCount} / {matches.length} terminés</span>
                            </div>
                            <div style={{ padding: '8px' }}>
                                {matches.map(m => {
                                    const isDone = !!m.winner_id;
                                    const isWinner1 = String(m.winner_id) === String(m.athlete1_id);
                                    const a1 = resolveMatchName(m.athlete1_id, m.placeholder1, allAthletes, order, m.source_match_id);
                                    const a2 = resolveMatchName(m.athlete2_id, m.placeholder2, allAthletes, order, m.source_match_id2);
                                    return (
                                        <div key={m.id} style={{ padding: '9px 10px', borderRadius: '8px', backgroundColor: isDone ? '#f8fafc' : 'white', marginBottom: '4px', border: '1px solid #f0f4f8', opacity: isDone ? 0.85 : 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', flexWrap: 'wrap' }}>
                                                <span style={{ backgroundColor: '#2d3748', color: 'white', padding: '2px 7px', borderRadius: '5px', fontSize: '11px', fontWeight: 'bold', flexShrink: 0 }}>Assaut {m.match_ref}</span>
                                                <span style={{ backgroundColor: '#edf2f7', color: '#4a5568', padding: '2px 7px', borderRadius: '5px', fontSize: '10px', fontWeight: '700', flexShrink: 0 }}>Ring {m.ring_id}</span>
                                                {isDone
                                                    ? <span style={{ backgroundColor: '#c6f6d5', color: '#276749', padding: '2px 7px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', marginLeft: 'auto', flexShrink: 0 }}>✓</span>
                                                    : <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', marginLeft: 'auto', flexShrink: 0 }}>À venir</span>
                                                }
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: '700', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isDone && isWinner1 ? '#c53030' : '#4a5568' }}>
                                                        {isDone && isWinner1 && '🏆 '}{a1.name}
                                                    </div>
                                                    {a1.club && <div style={{ fontSize: '10px', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a1.club}</div>}
                                                </div>
                                                <span style={{ color: '#cbd5e0', fontSize: '10px', flexShrink: 0, paddingTop: '1px' }}>vs</span>
                                                <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                                                    <div style={{ fontWeight: '700', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isDone && !isWinner1 ? '#2b6cb0' : '#4a5568' }}>
                                                        {isDone && !isWinner1 && '🏆 '}{a2.name}
                                                    </div>
                                                    {a2.club && <div style={{ fontSize: '10px', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{a2.club}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
}


// =========================================================
// VUE MON CLUB
// =========================================================
function MonClubView({ order, allAthletes, isMobile }) {
    const STORAGE_KEY = 'savatevent_monclub_selection';

    const [selectedIds, setSelectedIds] = useState(() => {
        try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
        catch { return []; }
    });
    const [search, setSearch] = useState('');
    const [showSelector, setShowSelector] = useState(true);

    useEffect(() => {
        if (selectedIds.length > 0) setShowSelector(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const saveSelection = (ids) => {
        setSelectedIds(ids);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    };

    const toggleAthlete = (id) => {
        const strId = String(id);
        const newIds = selectedIds.includes(strId) ? selectedIds.filter(i => i !== strId) : [...selectedIds, strId];
        saveSelection(newIds);
    };

    const sortedAthletes = [...allAthletes].sort((a, b) => {
        const na = `${a.nom || a.name || ''} ${a.prenom || a.firstname || ''}`.toLowerCase();
        const nb = `${b.nom || b.name || ''} ${b.prenom || b.firstname || ''}`.toLowerCase();
        return na.localeCompare(nb);
    });

    const filtered = search
        ? sortedAthletes.filter(a => `${a.nom || a.name || ''} ${a.prenom || a.firstname || ''} ${a.club || ''}`.toLowerCase().includes(search.toLowerCase()))
        : sortedAthletes;

    const myMatches = order
        .filter(m => selectedIds.includes(String(m.athlete1_id)) || selectedIds.includes(String(m.athlete2_id)))
        .sort((a, b) => parseInt(a.match_ref) - parseInt(b.match_ref));

    return (
        <div style={{ padding: isMobile ? '12px' : '20px' }}>
            {/* En-tête sélection */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#2d3748' }}>
                    Mes athlètes
                    {selectedIds.length > 0 && <span style={{ marginLeft: '8px', backgroundColor: '#3182ce', color: 'white', borderRadius: '12px', padding: '2px 8px', fontSize: '12px' }}>{selectedIds.length}</span>}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedIds.length > 0 && (
                        <button onClick={() => saveSelection([])} style={{ fontSize: '12px', color: '#e53e3e', background: 'none', border: '1px solid #feb2b2', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer' }}>
                            Effacer
                        </button>
                    )}
                    <button onClick={() => setShowSelector(!showSelector)} style={{ fontSize: '12px', color: '#3182ce', background: 'none', border: '1px solid #bee3f8', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer' }}>
                        {showSelector ? 'Masquer' : 'Modifier'}
                    </button>
                </div>
            </div>

            {/* Liste de sélection */}
            {showSelector && (
                <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '20px' }}>
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                        <input
                            placeholder="Rechercher un athlète ou un club..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                        />
                    </div>
                    <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                        {filtered.length === 0 && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0', fontSize: '13px' }}>Aucun athlète trouvé</div>
                        )}
                        {filtered.map(a => {
                            const strId = String(a.id);
                            const isSelected = selectedIds.includes(strId);
                            const name = `${(a.nom || a.name || '').toUpperCase()} ${a.prenom || a.firstname || ''}`.trim();
                            return (
                                <div key={a.id} onClick={() => toggleAthlete(a.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', cursor: 'pointer', backgroundColor: isSelected ? '#ebf8ff' : 'white', borderBottom: '1px solid #f0f4f8', transition: 'background 0.1s' }}>
                                    <div style={{ width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, border: isSelected ? '2px solid #3182ce' : '2px solid #cbd5e0', backgroundColor: isSelected ? '#3182ce' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {isSelected && <span style={{ color: 'white', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>✓</span>}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: isSelected ? '700' : '500', fontSize: '13px', color: '#2d3748' }}>{name}</div>
                                        {a.club && <div style={{ fontSize: '11px', color: '#a0aec0' }}>{a.club}</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Assauts des athlètes sélectionnés */}
            {selectedIds.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0' }}>
                    <Users size={36} style={{ marginBottom: '12px', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>Sélectionnez vos athlètes ci-dessus</p>
                </div>
            ) : myMatches.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0', fontStyle: 'italic' }}>Aucun assaut trouvé</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#718096', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.05em' }}>
                        {myMatches.length} assaut(s)
                    </div>
                    {myMatches.map(m => {
                        const isDone = !!m.winner_id;
                        const isA1Mine = selectedIds.includes(String(m.athlete1_id));
                        const myId = isA1Mine ? m.athlete1_id : m.athlete2_id;
                        const oppId = isA1Mine ? m.athlete2_id : m.athlete1_id;
                        const oppPlaceholder = isA1Mine ? m.placeholder2 : m.placeholder1;
                        const oppSourceId = isA1Mine ? m.source_match_id2 : m.source_match_id;
                        const mySide = isA1Mine ? 'red' : 'blue';
                        const isMyWin = isDone && String(m.winner_id) === String(myId);
                        const isMyLoss = isDone && !isMyWin;

                        const myInfo = getAthleteInfo(myId, allAthletes) || { name: '?', club: '' };
                        const oppInfo = resolveMatchName(oppId, oppPlaceholder, allAthletes, order, oppSourceId);

                        return (
                            <div key={m.id} style={{ borderRadius: '10px', border: `1px solid ${isMyWin ? '#9ae6b4' : isMyLoss ? '#feb2b2' : '#e2e8f0'}`, backgroundColor: isMyWin ? '#f0fff4' : isMyLoss ? '#fff5f5' : 'white', padding: isMobile ? '10px 12px' : '12px 14px' }}>
                                {/* Ligne de méta-info */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ backgroundColor: '#2d3748', color: 'white', padding: '3px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', letterSpacing: '0.03em' }}>
                                        ASSAUT {m.match_ref}
                                    </span>
                                    <span style={{ backgroundColor: '#edf2f7', color: '#4a5568', padding: '2px 7px', borderRadius: '5px', fontSize: '11px', fontWeight: '700' }}>
                                        Ring {m.ring_id}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#3182ce', fontWeight: 'bold' }}>{m.category_name}</span>
                                    <span style={{ fontSize: '10px', color: '#a0aec0' }}>{m.poule_label}</span>
                                    <span style={{ marginLeft: 'auto', fontWeight: 'bold', fontSize: '12px', color: isMyWin ? '#276749' : isMyLoss ? '#c53030' : '#92400e', backgroundColor: isMyWin ? '#c6f6d5' : isMyLoss ? '#fed7d7' : '#fef3c7', padding: '2px 8px', borderRadius: '8px', flexShrink: 0 }}>
                                        {isMyWin ? '🏆 Victoire' : isMyLoss ? '❌ Défaite' : 'À venir'}
                                    </span>
                                </div>
                                {/* Fighters */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: '900', fontSize: '14px', color: mySide === 'red' ? '#c53030' : '#2b6cb0' }}>{myInfo.name}</div>
                                        {myInfo.club && <div style={{ fontSize: '11px', color: '#a0aec0' }}>{myInfo.club}</div>}
                                    </div>
                                    <span style={{ color: '#cbd5e0', fontSize: '12px', flexShrink: 0, paddingTop: '2px' }}>vs</span>
                                    <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                                        <div style={{ fontWeight: '700', fontSize: '13px', color: '#4a5568' }}>{oppInfo.name}</div>
                                        {oppInfo.club && <div style={{ fontSize: '11px', color: '#a0aec0' }}>{oppInfo.club}</div>}
                                    </div>
                                </div>
                                {isDone && m.decision_type && (
                                    <div style={{ fontSize: '10px', color: '#718096', marginTop: '4px', fontStyle: 'italic' }}>
                                        {m.decision_type.replace(/_/g, ' ')}{(m.warnings_a1 > 0 || m.warnings_a2 > 0) && ` · AVT: ${m.warnings_a1}/${m.warnings_a2}`}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// =========================================================
// COMPOSANT PRINCIPAL
// =========================================================
export default function LiveManagement({ order, allAthletes, onUpdateWinner, categories, onReset, selectedCompetition, onToggleLive, readOnly }) {
    const isStarted = selectedCompetition?.is_started || false;
    const rings = [...new Set(order.map(m => m.ring_id))].filter(Boolean).sort((a, b) => a - b);
    const isMobile = useMobile();
    const [activeView, setActiveView] = useState('overview');

    const views = [
        { id: 'overview',  label: isMobile ? 'Vue'       : "Vue d'ensemble", icon: LayoutGrid },
        { id: 'ring',      label: isMobile ? 'Ring'      : 'Par Ring',        icon: List },
        { id: 'category',  label: isMobile ? 'Catég.'    : 'Par Catégorie',   icon: Tag },
        { id: 'monclub',   label: isMobile ? 'Club'      : 'Mon Club',        icon: Users },
        { id: 'results',   label: isMobile ? 'Résultats' : 'Résultats',       icon: Trophy }
    ];

    const noMatches = rings.length === 0;

    return (
        <div style={{ borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: 'white', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            {/* HEADER */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1a202c', padding: isMobile ? '10px 14px' : '14px 24px', flexWrap: 'wrap', gap: '8px', borderRadius: '12px 12px 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: isStarted ? '#48bb78' : '#e53e3e', boxShadow: isStarted ? '0 0 8px #48bb78' : 'none', flexShrink: 0 }} />
                    <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 'bold', color: 'white' }}>{isStarted ? 'SESSION ACTIVE' : 'SESSION EN PAUSE'}</span>
                    {readOnly && <div style={{ padding: '3px 8px', backgroundColor: '#4a5568', borderRadius: '20px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: 'white', flexShrink: 0 }}><Eye size={10} /> VUE</div>}
                </div>
                {!readOnly && (
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button onClick={() => onToggleLive(!isStarted)} style={{ backgroundColor: isStarted ? '#e53e3e' : '#38a169', color: 'white', border: 'none', padding: isMobile ? '7px 12px' : '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>
                            {isStarted ? 'ARRÊTER' : 'DÉMARRER'}
                        </button>
                        {!isStarted && <button onClick={onReset} style={{ backgroundColor: 'transparent', color: '#feb2b2', border: '1px solid #4a5568', padding: isMobile ? '6px 10px' : '7px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>RESET</button>}
                    </div>
                )}
            </div>

            {/* ONGLETS */}
            <div style={{ display: 'flex', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {views.map(({ id, label, icon: Icon }) => (
                    <button key={id} onClick={() => setActiveView(id)}
                        style={{ border: 'none', borderBottom: activeView === id ? '3px solid #3182ce' : '3px solid transparent', color: activeView === id ? '#3182ce' : '#718096', padding: isMobile ? '10px 7px' : '12px 16px', cursor: 'pointer', background: 'none', fontWeight: activeView === id ? 'bold' : 'normal', display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '5px', flexShrink: 0, fontSize: isMobile ? '11px' : '13px', whiteSpace: 'nowrap' }}>
                        <Icon size={isMobile ? 12 : 13} />
                        {label}
                    </button>
                ))}
            </div>

            {/* CONTENU */}
            <div style={{ backgroundColor: '#f7fafc', minHeight: isMobile ? '300px' : '400px', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                {noMatches && activeView !== 'results' && activeView !== 'monclub' ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#a0aec0' }}>
                        <p style={{ fontSize: '15px', marginBottom: '6px' }}>Aucun assaut généré.</p>
                        <p style={{ fontSize: '13px' }}>Configurez d'abord les poules et générez les matchs.</p>
                    </div>
                ) : (
                    <>
                        {activeView === 'overview' && <OverviewView order={order} allAthletes={allAthletes} rings={rings} isStarted={isStarted} readOnly={readOnly} onUpdateWinner={onUpdateWinner} isMobile={isMobile} />}
                        {activeView === 'ring'     && <RingDetailView order={order} allAthletes={allAthletes} rings={rings} isStarted={isStarted} readOnly={readOnly} onUpdateWinner={onUpdateWinner} isMobile={isMobile} />}
                        {activeView === 'category' && <CategoryView order={order} allAthletes={allAthletes} categories={categories} isMobile={isMobile} />}
                        {activeView === 'monclub'  && <MonClubView order={order} allAthletes={allAthletes} isMobile={isMobile} />}
                        {activeView === 'results'  && (
                            <div style={{ padding: isMobile ? '12px' : '20px' }}>
                                <ResultsView order={order} allAthletes={allAthletes} categories={categories} isMobile={isMobile} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
